import { emptySplitApi, RootState } from 'shared/store/store'
import { getActiveClientId } from 'shared/store/clients/selectors'
import { axiosErrorMessageHelper } from 'shared/utils/axiosErrorMessageHelper'

import { getMessages, getMessagesSection } from './selectors'
import { addToasterMessage } from 'shared/store/toaster'
import {
  GET_MESSAGE_LIST,
  GET_MESSAGE_LISTITEM,
  GET_MESSAGE_THREAD,
} from 'shared/constants/storeTags'
import { getUser } from 'shared/store/user/selectors'
import { getExtension, hasInvalidFileTypes } from 'shared/utils/file'
import {
  DetailedMessage,
  IMessageForm,
  MessageParams,
  MessagesQueryResult,
  MessageThread,
  ThreadsQueryResult,
} from '../../../models/Message'
import { MessagesSectionEnum } from '../../../models/enum'
import {
  MESSAGE_RESPONSIBLE_BOARD,
  MESSAGES_TAKE_COUNT,
  unsupportedMessageFileTypes,
} from '../../../constants'
import { getMessagesFilters } from '../filters/selectors'
import {
  getFilterQueryParams,
  getSectionQueryParams,
} from '../../../helpers/messages-query-helpers'
import { removeThreadFromList } from './reducer'
import { hasBoardRoleRoleForClient } from 'shared/utils/rolesHelper'

const errors = {
  messages: 'Noe gikk galt ved uthenting av meldinger',
  addMessage: 'Det oppstod en feil ved sending av melding',
}

const BASE_URL = 'chat/portal/'

const messagesService = emptySplitApi.injectEndpoints({
  /**
   *  QUERIES
   */
  endpoints: (build) => ({
    initRealTimeMessages: build.query<string, void>({
      async queryFn(_args, { getState }, _extraOptions, query) {
        const store = getState() as RootState
        const clientId = getActiveClientId(store)

        const res = await query({
          url: `${BASE_URL}${clientId}/realtime`,
          method: 'GET',
        })

        if (res.error)
          return {
            error: axiosErrorMessageHelper(res.error),
          }

        return {
          data: clientId || '',
        }
      },
    }),

    getMessages: build.query<ThreadsQueryResult, MessagesSectionEnum>({
      keepUnusedDataFor: 300, // Keep cache for 5 minutes
      providesTags: (_res, _err, section) => [
        { type: GET_MESSAGE_LIST, id: section },
      ],
      async queryFn(section, { getState }, _extraOptions, query) {
        const store = getState() as RootState
        const clientId = getActiveClientId(store)
        // @ts-expect-error todo
        const filters = getMessagesFilters(store)

        const res = await query({
          url: `${BASE_URL}${clientId}/threads?take=${MESSAGES_TAKE_COUNT}&skip=0${getSectionQueryParams(
            section
          )}${getFilterQueryParams(filters)}`,
          method: 'GET',
        })

        if (res.error) {
          return {
            error: axiosErrorMessageHelper(res.error, {
              fallbackError: errors.messages,
              withMessagePrefix: true,
            }),
          }
        }

        const data = res.data as ThreadsQueryResult

        return {
          data: {
            ...data,
            morePages: data.messageThreads.length < data.count,
          } as ThreadsQueryResult,
        }
      },
    }),

    getMoreMessages: build.query<ThreadsQueryResult, MessagesSectionEnum>({
      keepUnusedDataFor: 0,
      async queryFn(section, { dispatch, getState }, _extraOptions, query) {
        const store = getState() as RootState
        const clientId = getActiveClientId(store)
        // @ts-expect-error todo
        const filters = getMessagesFilters(store)
        const { data } = getMessages(section)(store)

        const skip = data?.messageThreads?.length as number

        const res = await query({
          url: `${BASE_URL}${clientId}/threads?take=${MESSAGES_TAKE_COUNT}&skip=${skip}${getSectionQueryParams(
            section
          )}${getFilterQueryParams(filters)}`,
          method: 'GET',
        })

        if (res.error) {
          return {
            error: axiosErrorMessageHelper(res.error, {
              fallbackError: errors.messages,
              withMessagePrefix: true,
            }),
          }
        }

        const newData = res.data as ThreadsQueryResult

        if (newData?.messageThreads?.length)
          dispatch(
            messagesService.util.updateQueryData(
              'getMessages',
              section,
              (draft) => {
                draft.morePages =
                  draft.messageThreads.length + newData.messageThreads.length <
                  newData.count
                draft.messageThreads.push(...newData.messageThreads)
              }
            )
          )

        return { data: newData }
      },
    }),

    // Fetches the 5 most recent threads in a section and adds it to state if it does not exist there
    getNewMessages: build.query<ThreadsQueryResult, MessagesSectionEnum>({
      keepUnusedDataFor: 0,
      async queryFn(section, { dispatch, getState }, _extraOptions, query) {
        const store = getState() as RootState
        const clientId = getActiveClientId(store)
        // @ts-expect-error todo
        const filters = getMessagesFilters(store)

        const res = await query({
          url: `${BASE_URL}${clientId}/threads?take=5&skip=0${getSectionQueryParams(
            section
          )}${getFilterQueryParams(filters)}`,
          method: 'GET',
        })

        if (res.error) {
          return {
            error: axiosErrorMessageHelper(res.error),
          }
        }

        const data = res.data as ThreadsQueryResult

        const newMessages = data.messageThreads

        if (newMessages?.length)
          dispatch(
            messagesService.util.updateQueryData(
              'getMessages',
              section,
              (draft) => {
                const newMessagesNotInList = newMessages.filter(
                  (x) => !draft.messageThreads.some(({ id }) => id === x.id)
                )

                draft.morePages =
                  draft.messageThreads.length + newMessagesNotInList.length <
                  data.count
                draft.messageThreads.unshift(...newMessagesNotInList)
              }
            )
          )

        return { data: undefined }
      },
    }),

    getMessage: build.query<MessageThread, Required<MessageParams>>({
      providesTags: (_res, _err, args) => [
        { type: GET_MESSAGE_LISTITEM, id: args.id },
      ],
      keepUnusedDataFor: 0,
      async queryFn({ id, section }, { getState }, _extraOptions, query) {
        const store = getState() as RootState
        const clientId = getActiveClientId(store)
        const messagesList = getMessages(section)(store)

        const data = messagesList.data as ThreadsQueryResult

        const itemFromList = data?.messageThreads?.find(
          (message) => message.id === id
        )

        if (itemFromList) return { data: itemFromList }

        const res = await query({
          url: `${BASE_URL}${clientId}/threads/${id}`,
          method: 'GET',
        })

        if (res.error) {
          return {
            error: axiosErrorMessageHelper(res.error, {
              fallbackError: errors.messages,
              withMessagePrefix: true,
            }),
          }
        }

        return {
          data: res.data as MessageThread,
        }
      },
    }),

    getMessageThread: build.query<DetailedMessage[], string>({
      keepUnusedDataFor: 300, // 5 minutes
      providesTags: (_res, _err, id) => [{ type: GET_MESSAGE_THREAD, id: id }],
      async queryFn(id, { getState, dispatch }, _extraOptions, query) {
        const store = getState() as RootState
        const clientId = getActiveClientId(store)
        // @ts-expect-error todo
        const section = getMessagesSection(store)
        const { data: currentUser } = getUser(store)

        const res = await query({
          url: `${BASE_URL}${clientId}/threads/${id}/messages?take=1000`,
          method: 'GET',
        })

        if (res.error) {
          return {
            error: axiosErrorMessageHelper(res.error, {
              fallbackError: errors.messages,
              withMessagePrefix: true,
            }),
          }
        }

        // Update seenBy in messagelist with current user if not already seen last message
        dispatch(
          messagesService.util.updateQueryData(
            'getMessages',
            section,
            (draft) => {
              const item = draft.messageThreads.find(
                (thread) => thread.id === id
              )
              if (
                item?.lastMessage?.seenBy &&
                !item.lastMessage.seenBy.some(
                  ({ system }) => system !== 'minside'
                )
              )
                item.lastMessage.seenBy.push({
                  seenDate: new Date().toISOString(),
                  system: 'portal',
                  userId: currentUser?.id || '',
                })
            }
          )
        )

        return { data: (res.data as MessagesQueryResult)?.messages }
      },
    }),

    /**
     *  MUTATIONS
     */
    createThread: build.mutation<string, IMessageForm>({
      invalidatesTags: [
        { type: GET_MESSAGE_LIST, id: MessagesSectionEnum.Answered },
      ],
      queryFn: async (payload, { dispatch, getState }, _extra, query) => {
        const store = getState() as RootState
        const clientId = getActiveClientId(store)
        const { data: user } = getUser(store)

        const mappedParticipants = payload.recipients.map(
          ({ nameregisterId, unitIds }) => ({
            NavneregisterId: nameregisterId,
            UnitId: unitIds || [],
          })
        )

        // Cut the calls into paralell jobs of maximum 50 participants
        const chunkedParticipants: {
          NavneregisterId: string
          UnitId: string[]
        }[][] = []

        const _chunkAmount = 50

        for (let i = 0; i < mappedParticipants.length; i += _chunkAmount) {
          chunkedParticipants.push(
            mappedParticipants.slice(i, i + _chunkAmount)
          )
        }

        // Filter out invalid files
        let newFiles = payload.attachments || []
        if (
          newFiles.length &&
          hasInvalidFileTypes(newFiles, unsupportedMessageFileTypes)
        ) {
          newFiles = newFiles.filter(
            (x) =>
              !unsupportedMessageFileTypes.includes(`.${getExtension(x.name)}`)
          )
        }

        const calls = chunkedParticipants.map((participants) => {
          const formData = new FormData()
          formData.append('subject', payload.subject)
          formData.append('message', payload.message)
          formData.append(
            'responsibleNavneregisterId',
            hasBoardRoleRoleForClient(user?.roles || [], clientId || '')
              ? user?.id || MESSAGE_RESPONSIBLE_BOARD
              : MESSAGE_RESPONSIBLE_BOARD
          )
          formData.append('participants', JSON.stringify(participants))

          if (newFiles.length) newFiles.map((x) => formData.append('files', x))

          return query({
            url: `${BASE_URL}${clientId}/threads`,
            method: 'POST',
            data: formData,
          })
        })

        const results = await Promise.all(calls)

        if (results.some(({ error }) => Boolean(error))) {
          dispatch(
            addToasterMessage({
              text: errors.addMessage,
              type: 'danger',
            })
          )
          return { error: errors.addMessage }
        }

        dispatch(
          addToasterMessage({
            text: `Meldingen${
              payload.recipients?.length > 1 ? 'e' : ''
            } ble opprettet`,
            type: 'success',
          })
        )

        return {
          data:
            results.length === 1
              ? (results[0].data as MessageThread)?.id
              : undefined,
        }
      },
    }),

    addMessage: build.mutation<
      void,
      Pick<IMessageForm, 'message' | 'attachments'> & {
        threadId: string
        section: MessagesSectionEnum
      }
    >({
      invalidatesTags: (_res, _err, { section }) => [
        ...(section === MessagesSectionEnum.UnAnswered
          ? [{ type: GET_MESSAGE_LIST, id: MessagesSectionEnum.Answered }]
          : []),
      ],
      queryFn: async (
        { attachments, message, threadId, section },
        { dispatch, getState },
        _extra,
        query
      ) => {
        const store = getState() as RootState
        const clientId = getActiveClientId(store)
        const { data: currentUser } = getUser(store)

        const formData = new FormData()
        formData.append('message', message)

        // Filter out invalid files
        if (attachments?.length) {
          let newFiles = attachments
          if (hasInvalidFileTypes(attachments, unsupportedMessageFileTypes)) {
            newFiles = newFiles.filter(
              (x) =>
                !unsupportedMessageFileTypes.includes(
                  `.${getExtension(x.name)}`
                )
            )
          }

          newFiles.map((x) => formData.append('files', x))
        }

        const result = await query({
          url: `${BASE_URL}${clientId}/threads/${threadId}/messages`,
          method: 'POST',
          data: formData,
        })

        if (result.error) {
          const error = axiosErrorMessageHelper(result.error, {
            fallbackError: errors.addMessage,
            withMessagePrefix: true,
          })
          dispatch(
            addToasterMessage({
              text: error || '',
              type: 'danger',
            })
          )
          return {
            error,
          }
        }

        const newMessage = result.data as DetailedMessage

        newMessage.user = {
          firstName: currentUser?.firstname || '',
          id: currentUser?.id || '',
          lastName: currentUser?.surname || '',
          roles: currentUser?.roles || [],
        }

        if (result.data) {
          // Add new message to front of thread
          dispatch(
            messagesService.util.updateQueryData(
              'getMessageThread',
              threadId,
              (draft) => {
                if (
                  !draft.some(
                    ({ messageId }) => messageId === newMessage.messageId
                  )
                )
                  draft.unshift(newMessage)
              }
            )
          )

          if (section === MessagesSectionEnum.UnAnswered) {
            dispatch(removeThreadFromList({ section, threadId }))
          } else {
            // Update threads list with message and time
            dispatch(
              messagesService.util.updateQueryData(
                'getMessages',
                section,
                (draft) => {
                  const item = draft.messageThreads.find(
                    (thread) => thread.id === threadId
                  )
                  if (item?.lastMessage) {
                    item.lastMessage = newMessage
                    item.lastActive = newMessage.updatedDate
                  }
                }
              )
            )
          }
        }
        return { data: undefined }
      },
    }),

    archiveOrUnarchiveThread: build.mutation<
      void,
      { threadId: string; archive: boolean; section: MessagesSectionEnum }
    >({
      invalidatesTags: (_res, _err, args) => [
        { type: GET_MESSAGE_LISTITEM, id: args.threadId },
        ...(!args.archive
          ? [
              { type: GET_MESSAGE_LIST, id: MessagesSectionEnum.Answered },
              { type: GET_MESSAGE_LIST, id: MessagesSectionEnum.UnAnswered },
            ]
          : [{ type: GET_MESSAGE_LIST, id: MessagesSectionEnum.Archived }]),
      ],
      queryFn: async (
        { threadId, archive, section },
        { dispatch, getState },
        _extra,
        query
      ) => {
        const store = getState() as RootState
        const clientId = getActiveClientId(store)

        const result = await query({
          url: `${BASE_URL}${clientId}/threads/${threadId}/archive`,
          method: 'PUT',
          data: {
            archived: archive,
          },
        })

        if (result.error) {
          const error =
            axiosErrorMessageHelper(result.error, {
              fallbackError: 'Kunne ikke arkivere meldingen',
              withMessagePrefix: true,
            }) || ''
          dispatch(
            addToasterMessage({
              text: error,
              type: 'danger',
            })
          )

          return { error }
        }

        dispatch(
          addToasterMessage({
            text: `Meldingen ble ${archive ? 'arkivert' : 'gjenåpnet'}`,
            type: 'success',
          })
        )

        dispatch(removeThreadFromList({ section, threadId }))

        return { data: undefined }
      },
    }),

    setResponsible: build.mutation<
      void,
      { threadId: string; userId: string; section: MessagesSectionEnum }
    >({
      invalidatesTags: (_res, _err, { threadId }) => [
        { type: GET_MESSAGE_LISTITEM, id: threadId },
      ],
      queryFn: async (
        { threadId, userId, section },
        { dispatch, getState },
        _extra,
        query
      ) => {
        const store = getState() as RootState
        const clientId = getActiveClientId(store)

        const result = await query({
          url: `${BASE_URL}${clientId}/threads/${threadId}/responsible`,
          method: 'PUT',
          data: {
            responsibleUserId: userId,
          },
        })

        if (result.error) {
          const error =
            axiosErrorMessageHelper(result.error, {
              fallbackError: 'Kunne ikke sette ansvarlig',
              withMessagePrefix: true,
            }) || ''
          dispatch(
            addToasterMessage({
              text: error,
              type: 'danger',
            })
          )

          return { error }
        }

        dispatch(
          messagesService.util.updateQueryData(
            'getMessages',
            section,
            (draft) => {
              const item = draft.messageThreads.find(
                (thread) => thread.id === threadId
              )
              if (item) item.responsibleNavneregisterId = userId
            }
          )
        )

        return { data: undefined }
      },
    }),

    markThreadAsAnswered: build.mutation<void, string>({
      invalidatesTags: (_res, _err, threadId) => [
        { type: GET_MESSAGE_LISTITEM, id: threadId },
        { type: GET_MESSAGE_LIST, id: MessagesSectionEnum.Answered },
      ],
      queryFn: async (threadId, { dispatch, getState }, _extra, query) => {
        const store = getState() as RootState
        const clientId = getActiveClientId(store)

        const result = await query({
          url: `${BASE_URL}${clientId}/threads/${threadId}/seen`,
          method: 'PUT',
          data: {
            seen: true,
          },
        })

        if (result.error) {
          const error =
            axiosErrorMessageHelper(result.error, {
              fallbackError: 'Kunne ikke markere som besvart',
              withMessagePrefix: true,
            }) || ''
          dispatch(
            addToasterMessage({
              text: error,
              type: 'danger',
            })
          )

          return { error }
        }

        // Remove the message from list
        dispatch(
          removeThreadFromList({
            section: MessagesSectionEnum.UnAnswered,
            threadId,
          })
        )

        dispatch(
          addToasterMessage({
            text: `Meldingen ble flyttet til mappen "Besvart"`,
            type: 'success',
          })
        )

        return { data: undefined }
      },
    }),
  }),
})
export default messagesService
